import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  Add,
  ManageAccounts,
  Edit,
  AccountBalanceOutlined,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import DataTable from '@/components/DataTable/DataTable';
import Button from '@/components/CustomButtons/Button';
import { Chip } from '@mui/material';
import { useFetchSmartPayables } from '@/api/hooks/smart-payables/useFetchSmartPayables';
import { useGetManagedAccounts } from '@/api/hooks/managed-accounts/useGetManagedAccounts';
import { MANAGED_ACCOUNT_TYPE } from '@/views/ManagedAccounts/managedAccount.constants';
import { toKebabCase } from '@/utils/string.util';

export default function SmartPayables({ organizationId }) {
  const { t } = useTranslation();
  const [tableFilters, setTableFilters] = useState({});

  const { smartPayables, loading, refetch } = useFetchSmartPayables({
    organizationId,
  });

  const {
    managedAccounts,
    error: managedAccountsError,
  } = useGetManagedAccounts(MANAGED_ACCOUNT_TYPE.POSITIVE_PAY);

  const managedAccountNamesById = useMemo(
    () =>
      new Map(
        managedAccounts.map((managedAccount) => [
          managedAccount.id,
          managedAccount.name,
        ]),
      ),
    [managedAccounts],
  );

  const filteredData = useMemo(() => {
    let filtered = smartPayables;

    if (tableFilters.search) {
      filtered = filtered.filter(
        (item) =>
          item.name &&
          item.name
            .toString()
            .toLowerCase()
            .includes(tableFilters.search.toLowerCase()),
      );
    }

    return filtered;
  }, [smartPayables, tableFilters]);

  const handleTableFilter = (filterKey, filterValue) => {
    setTableFilters((prev) => ({
      ...prev,
      [filterKey]: filterValue,
    }));
  };

  const getFacilities = (account) => {
    return account.facilities || [];
  };

  const getCheckManagementLink = (row) => {
    return organizationId
      ? `/admin/checkManagement/management?organizationId=${organizationId}&id=${row.id}`
      : `/admin/checkManagement/management?id=${row.id}`;
  };

  const getUpdateSmartPayablesLink = (row) => {
    return organizationId
      ? `/admin/checkManagement/edit/${row.id}?organizationId=${organizationId}`
      : `/admin/checkManagement/edit/${row.id}`;
  };

  const smartPayablesColumnFilters = useMemo(
    () => [
      {
        key: 'search',
        label: t('smartPayablesManagement.filters.searchAccountName'),
        filterType: 'search',
        placeholder: t(
          'smartPayablesManagement.filters.searchAccountNamePlaceholder',
        ),
        debounce: true,
        debounceDelay: 500,
      },
    ],
    [t],
  );

  const smartPayablesColumns = [
    {
      key: 'bank_alias',
      label: t('smartPayablesManagement.table.columns.bankAlias'),
      sortable: false,
    },
    {
      key: 'name',
      label: t('smartPayablesManagement.table.columns.accountName'),
      sortable: false,
    },
    {
      key: 'bank_account',
      label: t('smartPayablesManagement.table.columns.bankAccount'),
      sortable: false,
      render: (value) => {
        if (!value) return t('common.labels.notAvailable');

        return (
          <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
            {value}
          </Typography>
        );
      },
    },
    {
      key: 'routing_number',
      label: t('smartPayablesManagement.table.columns.routingNumber'),
      sortable: false,
      render: (value) => (
        <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
          {value || t('common.labels.notAvailable')}
        </Typography>
      ),
    },
    {
      key: 'bank_name',
      label: t('smartPayablesManagement.table.columns.bankName'),
      sortable: false,
      render: (value) => (
        <Typography variant='body2'>
          {value || t('common.labels.notAvailable')}
        </Typography>
      ),
    },
    {
      key: 'managed_account_id',
      label: t('smartPayablesManagement.table.columns.managedAccount'),
      sortable: false,
      render: (value) => {
        if (!value) {
          return null;
        }

        const name = managedAccountNamesById.get(value);

        return (
          <Tooltip
            title={
              name
                ? ''
                : managedAccountsError ||
                  t('smartPayablesManagement.table.managedAccountNameUnknown')
            }>
            <Chip
              label={
                name ||
                `${t(
                  'smartPayablesManagement.table.managedAccountId',
                )}: ${value}`
              }
              size='small'
              color='primary'
              variant='filled'
              icon={<AccountBalanceOutlined />}
              sx={{
                color: 'white',
                '& .MuiChip-label': { color: 'white' },
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'organization_name',
      label: t('smartPayablesManagement.table.columns.organization'),
      sortable: false,
      render: (value, row) => (
        <Typography variant='body2'>
          {value ||
            `${t('smartPayablesManagement.table.organizationId')}: ${
              row.organization_id || t('common.labels.notAvailable')
            }`}
        </Typography>
      ),
    },
    {
      key: 'created_at',
      label: t('smartPayablesManagement.table.columns.createdDate'),
      sortable: false,
      format: 'date',
    },
    {
      key: 'facilities',
      label: t('smartPayablesManagement.table.columns.facilities'),
      sortable: false,
      render: (value, row) => {
        const facilities = getFacilities(row);
        const facilitiesTooltip =
          facilities.length > 0 ? (
            <Box sx={{ p: 1 }}>
              <Typography
                variant='subtitle2'
                sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('smartPayablesManagement.table.columns.facilities')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                }}>
                {facilities.map((facility) => (
                  <Chip
                    key={facility.facilityId}
                    label={`${facility.facilityName} (${facility.facilityId})`}
                    size='small'
                    color='primary'
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            t('smartPayablesManagement.table.noFacilities')
          );

        return (
          <Tooltip title={facilitiesTooltip} arrow placement='top'>
            <Typography
              variant='body1'
              component='span'
              sx={{
                fontWeight: 'bold',
                fontSize: 16,
                padding: '6px 12px',
                borderRadius: '16px',
                cursor: 'pointer',
                color:
                  facilities.length > 0 ? 'primary.main' : 'text.secondary',
              }}>
              {facilities.length}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      key: 'actions',
      label: t('smartPayablesManagement.table.columns.actions'),
      sortable: false,
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('smartPayablesManagement.actions.editAccount')}>
            <IconButton
              component={Link}
              to={getUpdateSmartPayablesLink(row)}
              size='small'>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('smartPayablesManagement.actions.manageAccount')}>
            <IconButton
              component={Link}
              to={getCheckManagementLink(row)}
              data-testid={`manageAccount-${toKebabCase(row.name ?? '')}`}
              size='small'>
              <ManageAccounts />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const createSmartPayablesLink = organizationId
    ? `/admin/checkManagement/create?organizationId=${organizationId}`
    : '/admin/checkManagement/create';

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
        <Button
          component={Link}
          to={createSmartPayablesLink}
          color='primary'
          variant='contained'
          startIcon={<Add sx={{ color: 'white' }} />}>
          {t('smartPayablesManagement.createAccountButton')}
        </Button>
      </Box>
      <Box
        sx={{
          width: '100%',
          overflow: 'auto',
          maxHeight: '70vh',
          position: 'relative',
        }}>
        <DataTable
          columns={smartPayablesColumns}
          testData={loading ? [] : filteredData}
          enableColumnPicker={true}
          enableSorting={false}
          enablePagination={false}
          enableFilters={true}
          persistenceKey='smartpayables'
          columnFilters={smartPayablesColumnFilters}
          onRefresh={refetch}
          onFilter={handleTableFilter}
          externalFiltering={tableFilters}
          externalLoading={loading}
          tableProps={{
            sx: {
              '& .MuiTableCell-root': {
                padding: '8px 16px',
              },
              '& .MuiTableRow-root': {
                height: 'auto',
              },
              '& .MuiTableContainer-root': {
                maxHeight: 'none',
                overflow: 'visible',
              },
            },
          }}
          renderEmptyState={() => (
            <tr>
              <td
                colSpan={smartPayablesColumns.length}
                style={{
                  textAlign: 'center',
                  padding: '3rem',
                }}>
                <Typography variant='h6' color='text.secondary'>
                  {t('smartPayablesManagement.emptyState.title')}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 1 }}>
                  {t('smartPayablesManagement.emptyState.description')}
                </Typography>
              </td>
            </tr>
          )}
        />
      </Box>
    </>
  );
}
